import { type Trello } from "../types/trello"

// The date, the mode tabs, the duration or the pair of time fields, the quick
// durations, the note and the buttons (see card-back-section/LogTimeForm),
// plus room for the validation line that appears under them.
//
// Fixed at open — a popup can't resize itself the way the card back does with
// t.sizeTo — so this is the taller of the two modes, and span mode, which has
// no quick durations, simply leaves a little more space at the bottom.
const LOG_TIME_POPUP_HEIGHT = 330

/**
 * Opens the log-time screen, for a new entry or an existing one.
 *
 * The card button is the only way to log the *first* entry on a card: the card
 * back section stays away until a card has time on it, so an affordance that
 * lived only there could never be used to create one. Editing comes here too,
 * so there is one form rather than two that can drift apart.
 *
 * Pass `mouseEvent` to anchor the popup to the click.
 */
export const openLogTimePopup = (
  t: Trello.PowerUp.IFrame,
  mouseEvent?: MouseEvent,
  entryId?: number
) =>
  t.popup({
    title: entryId ? "Edit time" : "Log time",
    url: t.signUrl("./log-time.html"),
    // Read back inside the iframe with `t.arg("entryId")`.
    args: entryId ? { entryId } : undefined,
    height: LOG_TIME_POPUP_HEIGHT,
    mouseEvent,
  })

/**
 * The ⋯ menu on a card-back entry: edit it, or delete it.
 *
 * Trello's own list popup rather than a menu drawn in the iframe. The card back
 * is a fixed-height frame — a menu of our own would either be clipped by it or
 * force it taller to make room for something that is usually shut — and this
 * one comes out looking like every other menu on the card.
 *
 * Delete asks first, through Trello's confirm popup, which replaces this one
 * with a Back chevron to here. It really does delete — the API removes the row
 * — and there is nothing on the card back that undoes it, so the confirm is
 * the only thing standing between a misplaced click and losing the entry.
 */
export const openEntryMenu = (
  t: Trello.PowerUp.IFrame,
  mouseEvent: MouseEvent,
  entryId: number,
  onDelete: () => void
) =>
  t.popup({
    title: "Time entry",
    items: [
      {
        text: "Edit",
        // No mouseEvent: inside a popup callback Trello anchors to the popup
        // itself, and passing the original click would point the new one back
        // at the row the menu was opened from.
        callback: (tc: Trello.PowerUp.IFrame) => openLogTimePopup(tc, undefined, entryId),
      },
      {
        text: "Delete",
        callback: (tc: Trello.PowerUp.IFrame) =>
          tc.popup({
            type: "confirm",
            title: "Delete time entry?",
            message: "This removes the entry from the card.",
            confirmText: "Delete",
            confirmStyle: "danger",
            onConfirm: async (tcc: Trello.PowerUp.IFrame) => {
              onDelete()
              await tcc.closePopup()
            },
            // No mouseEvent, for the same reason as Edit above: it would put
            // the confirm back over the row instead of over this menu.
          }),
      },
    ],
    mouseEvent,
  })
